import { Injectable, Inject } from '@angular/core';

import { HttpClient, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { catchError, map } from 'rxjs/operators';

import {
  User,
  JobRole,
  EntitlementRole,
  TinAccess,
  AssignUsersBulkRequestBody,
  AssignUsersIndividualRequestBody,
  AddUsersBulkRequestBody,
  AddUsersIndividualRequestBody,
  UserTinLob,
} from './manage-access.model';

import { SERVICE_CONSUMER_CODE, TIN_STATUS } from './manage-access.constant';

import {
  ServiceError,
  checkValidResponse,
  logError,
  TinLob,
} from '@cigna/chcp/shared/util';

@Injectable({ providedIn: 'root' })
export class ManageUserAccessService {
  public delgationApi: string;
  public preferencesApi: string;
  public mCenterApi: string;

  constructor(
    private http: HttpClient,
    @Inject('secureApi') secureApi: string,
  ) {
    this.delgationApi = `${secureApi}/delegation/v2`;
    this.preferencesApi = `${secureApi}/preferences/v2`;
    this.mCenterApi = `${secureApi}/mcenter/v1`;
  }

  getAllUsers(): Observable<User[] | ServiceError> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .get(`${this.delgationApi}/wam/users`, { params })
      .pipe(
        map(checkValidResponse),
        map(this.parseUserResponse),
        catchError(logError),
      );
  }

  getPendingUsers(): Observable<User[] | ServiceError> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .get(`${this.delgationApi}/wam/users/pending`, { params })
      .pipe(
        map(checkValidResponse),
        map(this.parseUserResponse),
        catchError(logError),
      );
  }

  getApprovedUsers(): Observable<User[] | ServiceError> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .get(`${this.delgationApi}/wam/users/approved`, { params })
      .pipe(
        map(checkValidResponse),
        map(this.parseUserResponse),
        catchError(logError),
      );
  }

  private parseUserResponse(res: any): User[] {
    return res.users
      .map(
        // TODO: resolve CHCP disables by bot
        // eslint-disable-next-line sonarjs/cognitive-complexity
        (user: any): User => ({
          id: user.id
            ? user.id.trim()
            : user.temporaryUserName
            ? user.temporaryUserName.trim()
            : '',
          ssoId: user.id?.trim(),
          firstName: user.firstName.trim(),
          lastName: user.lastName.trim(),
          temporaryUserName: user.temporaryUserName?.trim(),
          temporaryPassword: user.temporaryPassword?.trim(),
          userName: user.userName.trim(),
          email: user.email.trim(),
          phone: user.phone?.trim(),
          jobRole: user.jobRole.trim(),
          jobRoleId: user.jobRoleId,
          status: user.status?.trim(),
          requestedDate: user.requestedDate || undefined,
          lob: user.lob,
          lobPref: user.lobPref,
          tinCount: user.tinCount || 0,
          tins: user.tins
            ? user.tins.map((tin: UserTinLob) => ({
                name: tin.name?.trim() || '',
                status: tin.status?.trim() || '',
                tin: tin.tin.trim(),
                lob: tin.lob,
              }))
            : [],
          hasTinsAwaitingApproval: user.tins
            ? user.tins.filter(
                (tin: UserTinLob) => tin.status === TIN_STATUS.unassigned,
              ).length > 0
            : false,
          hasTinsExisting: user.tins
            ? user.tins.filter(
                (tin: UserTinLob) => tin.status === TIN_STATUS.assigned,
              ).length > 0
            : false,
        }),
      ) // sort alphabetically on lastName
      .sort((a: User, b: User) => {
        if (a.lastName < b.lastName) {
          return -1;
        }
        if (a.lastName > b.lastName) {
          return 1;
        }
        return 0;
      });
  }

  getUserTinEntitlements(users: string): Observable<any[]> {
    const params: HttpParams = new HttpParams()
      .set('user', users)
      .set('consumerCode', SERVICE_CONSUMER_CODE);

    return this.http
      .get(`${this.delgationApi}/user/entitlements`, { params })
      .pipe(
        map(checkValidResponse),
        map(this.parseUserTinEntitlements),
        catchError(logError),
      );
  }

  private parseUserTinEntitlements(res: any) {
    return res.users.map((user: User) => ({
      id: user.id,
      tins: user.tins.map((tin) => ({
        tin: tin.tin,
        lob: tin.lob,
        entitlements: tin.entitlements
          ? tin.entitlements.map((entitlement) => ({
              id: entitlement.id || null,
              name: entitlement.name ? entitlement.name.trim() : '',
              description: entitlement.description
                ? entitlement.description.trim()
                : '',
              functions: entitlement.functions
                ? entitlement.functions
                    .filter((f) => f.name !== 'Claim status notifications') // temp until api can remove from response
                    .map((f) => ({
                      name: f.name.trim(),
                    }))
                : undefined,
            }))
          : [],
      })),
    }));
  }

  getJobRoles(): Observable<JobRole[] | ServiceError> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .get(`${this.delgationApi}/jobRoles`, { params })
      .pipe(
        map(checkValidResponse),
        map(this.parseJobRoleResponse),
        catchError(logError),
      );
  }

  private parseJobRoleResponse(res: any): JobRole[] {
    return res.jobRoles.map((role: any) => ({
      id: role.id,
      name: role.name.trim(),
      description: role.description.trim(),
      entitlements: role.entitlements.map((entitlement: EntitlementRole) => ({
        id: entitlement.id,
        name: entitlement.name.trim(),
        description: entitlement.description
          ? entitlement.description.trim()
          : '',
        functions: entitlement.functions
          ? entitlement.functions.map((f) => ({
              name: f.name.trim(),
            }))
          : undefined,
      })),
    }));
  }

  getEntitlements(): Observable<EntitlementRole[] | ServiceError> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .get(`${this.delgationApi}/entitlementsOnLob`, { params })
      .pipe(
        map(checkValidResponse),
        map(this.parseEntitlementsResponse),
        catchError(logError),
      );
  }

  private parseEntitlementsResponse(res: any): EntitlementRole[] {
    return res.entitlements.map((role: any) => ({
      id: role.id,
      name: role.name.trim(),
      description: role.description.trim(),
      functions: role.functions
        ? role.functions.map((f: any) => ({
            name: f.name.trim(),
          }))
        : undefined,
    }));
  }

  getAllTins() {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .get(`${this.delgationApi}/wam/tins`, { params })
      .pipe(
        map(checkValidResponse),
        map(this.parseTinResponse),
        catchError(logError),
      );
  }

  private parseTinResponse(res: any): TinAccess[] {
    return res.tins.map((tinObject: any) => ({
      tin: tinObject.tin.trim(),
      name: tinObject.name.trim(),
      lob: tinObject.lob,
      entitlements: tinObject.entitlements,
      sanctndInd: tinObject.sanctndInd,
      govrnm: tinObject.govrnm,
    }));
  }

  assignAccess(
    request: AssignUsersIndividualRequestBody | AssignUsersBulkRequestBody,
    assignmentMethod: string,
  ) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    const apiEndpoint =
      assignmentMethod === 'bulk'
        ? `${this.delgationApi}/group/tins/assignAccess`
        : `${this.delgationApi}/assignAccess`;

    return this.http
      .put(apiEndpoint, request, {
        params,
      })
      .pipe(map(checkValidResponse), catchError(logError));
  }

  setMcPreferences(request: {
    [key: string]: { added: string[]; removed: string[] };
  }) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .post(`${this.mCenterApi}/preference`, request, {
        params,
      })
      .pipe(map(checkValidResponse), catchError(logError));
  }

  addUsers(
    request: AddUsersIndividualRequestBody | AddUsersBulkRequestBody,
    lobType: string,
    assignmentMethod: string,
  ) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    const apiEndpoint =
      assignmentMethod === 'bulk'
        ? `${this.delgationApi}/wam/group/tins/addUsers`
        : `${this.delgationApi}/wam/addUsers`;

    return this.http
      .post(apiEndpoint, request, {
        params,
      })
      .pipe(
        map(checkValidResponse),
        map(this.parseAddUsersResponse),
        catchError(logError),
      );
  }

  private parseAddUsersResponse(res: any) {
    return res.users.map((user: any) => ({
      firstName: user.firstName.trim(),
      lastName: user.lastName.trim(),
      email: user.email.trim(),
      phone: user.phone.trim(),
      jobRole: user.jobRole.trim(),
      lob: user.lob,
      lobPref: user.lobPref,
      temporaryUserName: user.temporaryUserName.trim(),
      temporaryPassword: user.temporaryPassword.trim(),
    }));
  }

  removeAccess(userId: string, tins: TinLob[]) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    const requestBody = {
      tins: tins.map((tin) => ({ tinNumber: tin.tin, lob: tin.lob })),
    };

    return this.http
      .put(`${this.delgationApi}/user/${userId}/removeAccess`, requestBody, {
        params,
      })
      .pipe(
        map(checkValidResponse),
        map(this.validRemoveAccess),
        catchError(logError),
      );
  }

  private validRemoveAccess(res: any) {
    if (!res.vendorStatus || res.vendorStatus.code === 0) {
      return res;
    }
    throw res.vendorStatus;
  }

  resetPassword(userId: string): Observable<string> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .put(
        `${this.preferencesApi}/user/${userId}/resetPassword`,
        {}, // request body empty for now
        {
          params,
        },
      )
      .pipe(
        map(checkValidResponse),
        map((res) => res.userInfo.tempPassword),
        catchError(logError),
      );
  }

  getUserTinLob(userId: string): Observable<UserTinLob[]> {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .get(`${this.delgationApi}/user/${userId}/all-tins`, {
        params,
      })
      .pipe(
        map(checkValidResponse),
        map((res) => res.tins),
        catchError(logError),
      );
  }

  updateUserTinLobPref(userId: string, lobPref: string) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      SERVICE_CONSUMER_CODE,
    );

    return this.http
      .put(
        `${this.delgationApi}/user/${userId}/update-lob-pref`,
        { lobPref },
        {
          params,
        },
      )
      .pipe(map(checkValidResponse), catchError(logError));
  }
}
